import { Link } from "@inertiajs/react"
import {
  ArrowRight,
  BarChart3,
  BellRing,
  Bot,
  Building2,
  ChevronDown,
  CircleHelp,
  FileText,
  Globe2,
  GraduationCap,
  Headphones,
  Layers3,
  Mail,
  Menu,
  MessageSquareText,
  Network,
  ShieldCheck,
  UsersRound,
  X,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { BrandMark } from "@/components/brand-mark"
import { AnimatedThemeToggler } from "@/components/marketing/animated-theme-toggler"
import type { MarketingLocale } from "@/hooks/use-marketing-locale"
import { marketingLocales, useMarketingLocale } from "@/hooks/use-marketing-locale"
import { productEntry } from "@/lib/product-entry"
import { cn } from "@/lib/utils"

type MenuName = "product" | "solutions" | "industries" | "resources"

const menuItems = {
  product: [
    {
      href: "/product#case-workspace",
      title: "Case workspace",
      description: "One owner, one structured report, and every resolution state.",
      icon: MessageSquareText,
    },
    {
      href: "/product#evidence",
      title: "Evidence timelines",
      description: "Open the approved record behind every material finding.",
      icon: BarChart3,
    },
    {
      href: "/product#actions",
      title: "Governed actions",
      description: "Preview, approve, execute, verify, and audit bounded actions.",
      icon: Bot,
    },
    {
      href: "/product#playbooks",
      title: "Versioned playbooks",
      description: "Reuse developer-confirmed investigations without widening authority.",
      icon: Network,
    },
  ],
  solutions: [
    {
      href: "/how-it-works#support",
      title: "Support teams",
      description: "Report issues quickly and reach an evidence-backed next step.",
      icon: Headphones,
    },
    {
      href: "/how-it-works#operations",
      title: "Customer operations",
      description: "Own investigations, approvals, and repeatable resolution paths.",
      icon: GraduationCap,
    },
    {
      href: "/how-it-works#engineering",
      title: "Engineering governance",
      description: "Control data access, actions, playbooks, and code review boundaries.",
      icon: UsersRound,
    },
  ],
  industries: [
    {
      href: "/product#b2b-saas",
      title: "Specialist B2B SaaS",
      description: "Reduce recurring developer interruptions across support and success.",
      icon: Globe2,
    },
    {
      href: "/product#billing-workflows",
      title: "Billing-heavy software",
      description: "Investigate account, subscription, payment, and entitlement mismatches.",
      icon: GraduationCap,
    },
    {
      href: "/product#workflow-operations",
      title: "Workflow platforms",
      description: "Explain failed jobs and stuck state transitions with cited evidence.",
      icon: Building2,
    },
  ],
  resources: [
    {
      href: "/security",
      title: "Security",
      description: "Review access, data protection and operational controls.",
      icon: ShieldCheck,
    },
    {
      href: "/product",
      title: "Documentation",
      description: "Product, connector, and governance guidance for Resolto.",
      icon: FileText,
    },
    {
      href: "/how-it-works",
      title: "Help centre",
      description: "Answers for setup, cases, approvals, and demo workflows.",
      icon: CircleHelp,
    },
    {
      href: "/integrations",
      title: "Product updates",
      description: "Follow verified changes to the Resolto product.",
      icon: BellRing,
    },
    {
      href: "/contact",
      title: "Contact",
      description: "Talk through one support workflow, its evidence, and controls.",
      icon: Mail,
    },
  ],
} as const

export function MarketingNav() {
  const { locale, updateLocale, t } = useMarketingLocale()
  const [panel, setPanel] = useState<MenuName | null>(null)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [floating, setFloating] = useState(false)
  const [logoReady, setLogoReady] = useState(false)
  const [logoRunning, setLogoRunning] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const headerRef = useRef<HTMLElement>(null)
  const logoMarkRef = useRef<HTMLSpanElement>(null)
  const activeTriggerRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    const logo = logoMarkRef.current?.querySelector("img")

    if (!logo) {
      return
    }

    let cancelled = false
    const startEntrance = () => {
      const decoded =
        typeof logo.decode === "function" ? logo.decode().catch(() => undefined) : Promise.resolve()

      void decoded.then(() => {
        if (!cancelled) {
          setLogoReady(true)
        }
      })
    }

    if (logo.complete && logo.naturalWidth > 0) {
      startEntrance()
    } else {
      logo.addEventListener("load", startEntrance, { once: true })
    }

    return () => {
      cancelled = true
      logo.removeEventListener("load", startEntrance)
    }
  }, [])

  useEffect(() => {
    if (!logoReady) {
      return
    }

    let playFrame = 0
    const paintFrame = window.requestAnimationFrame(() => {
      playFrame = window.requestAnimationFrame(() => setLogoRunning(true))
    })

    return () => {
      window.cancelAnimationFrame(paintFrame)

      if (playFrame) {
        window.cancelAnimationFrame(playFrame)
      }
    }
  }, [logoReady])

  useEffect(() => {
    let ticking = false
    const update = () => {
      setFloating(window.scrollY > 80)
      ticking = false
    }
    const onScroll = () => {
      if (ticking) {
        return
      }

      ticking = true
      window.requestAnimationFrame(update)
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    update()

    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    if (!panel && !languageOpen) {
      return
    }

    const close = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) {
        setPanel(null)
        setLanguageOpen(false)
      }
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return
      }

      setPanel(null)
      setLanguageOpen(false)
      activeTriggerRef.current?.focus()
    }

    document.addEventListener("pointerdown", close)
    document.addEventListener("keydown", handleEscape)

    return () => {
      document.removeEventListener("pointerdown", close)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [languageOpen, panel])

  const openPanel = (name: MenuName, trigger: HTMLButtonElement) => {
    activeTriggerRef.current = trigger
    setLanguageOpen(false)
    setPanel(name)
  }

  const openMobile = () => dialogRef.current?.showModal()
  const closeMobile = () => dialogRef.current?.close()

  return (
    <header ref={headerRef} className={cn("marketing-nav", floating && "is-floating")}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Pointer leave only dismisses the hover menu; buttons, Escape, and outside clicks provide keyboard-equivalent dismissal. */}
      <div className="marketing-nav__inner" role="presentation" onMouseLeave={() => setPanel(null)}>
        <Link href="/" className="marketing-wordmark" aria-label="Resolto home">
          <span
            ref={logoMarkRef}
            className={cn(
              "marketing-wordmark__mark",
              logoReady && "is-loaded",
              logoRunning && "is-running",
            )}
          >
            <BrandMark />
          </span>
          <span>Resolto</span>
        </Link>

        <nav className="marketing-nav__links" aria-label="Primary navigation">
          {(
            [
              ["product", "nav.product"],
              ["solutions", "nav.solutions"],
              ["industries", "nav.industries"],
            ] as const
          ).map(([name, label]) => (
            <button
              key={name}
              type="button"
              className="marketing-nav__link"
              aria-expanded={panel === name}
              aria-controls={`marketing-${name}-menu`}
              onMouseEnter={(event) => openPanel(name, event.currentTarget)}
              onFocus={(event) => openPanel(name, event.currentTarget)}
              onClick={(event) => openPanel(name, event.currentTarget)}
            >
              {t(label)}
              <ChevronDown className="size-3.5" aria-hidden="true" />
            </button>
          ))}
          <Link className="marketing-nav__link" href="/pricing" onMouseEnter={() => setPanel(null)}>
            {t("nav.pricing")}
          </Link>
          <button
            type="button"
            className="marketing-nav__link"
            aria-expanded={panel === "resources"}
            aria-controls="marketing-resources-menu"
            onMouseEnter={(event) => openPanel("resources", event.currentTarget)}
            onFocus={(event) => openPanel("resources", event.currentTarget)}
            onClick={(event) => openPanel("resources", event.currentTarget)}
          >
            {t("footer.resources")}
            <ChevronDown className="size-3.5" aria-hidden="true" />
          </button>
        </nav>

        <div className="marketing-nav__actions">
          <div className="marketing-language">
            <button
              type="button"
              className="marketing-icon-button"
              aria-label={t("nav.language")}
              aria-expanded={languageOpen}
              onClick={(event) => {
                activeTriggerRef.current = event.currentTarget
                setPanel(null)
                setLanguageOpen((open) => !open)
              }}
            >
              <Globe2 className="size-[1.125rem]" aria-hidden="true" />
              <span className="marketing-icon-button__text">
                {marketingLocales.find(({ code }) => code === locale)?.short}
              </span>
            </button>
            {languageOpen && (
              <div className="marketing-language__menu" role="menu">
                {marketingLocales.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    role="menuitemradio"
                    aria-checked={locale === code}
                    className="marketing-language__option"
                    onClick={() => {
                      updateLocale(code as MarketingLocale)
                      setLanguageOpen(false)
                    }}
                  >
                    <span>{label}</span>
                    {locale === code && <span aria-hidden="true">●</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <AnimatedThemeToggler className="marketing-theme-button" />
          <Link className="marketing-nav__demo" href="/contact">
            {t("cta.secondary")}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
          <Link className="marketing-primary-button" href={productEntry}>
            {t("nav.createAccount")}
          </Link>
          <AnimatedThemeToggler className="marketing-mobile-theme" />
          <button
            type="button"
            className="marketing-mobile-toggle"
            aria-label={t("nav.menu")}
            onClick={openMobile}
          >
            <Menu className="size-5" aria-hidden="true" />
          </button>
        </div>

        {panel && (
          <div key={panel} id={`marketing-${panel}-menu`} className="marketing-mega-menu">
            <div className="marketing-mega-menu__grid">
              {menuItems[panel].map(({ href, title, description, icon: Icon }) => (
                <Link
                  key={href}
                  href={href}
                  className="marketing-mega-menu__item"
                  onClick={() => setPanel(null)}
                >
                  <Icon className="size-5" aria-hidden="true" />
                  <span>
                    <strong>{title}</strong>
                    <small>{description}</small>
                  </span>
                </Link>
              ))}
            </div>
            <Link
              href={
                panel === "product"
                  ? "/product"
                  : panel === "solutions"
                    ? "/how-it-works"
                    : panel === "industries"
                      ? "/product#b2b-saas"
                      : "/security"
              }
              className="marketing-mega-menu__feature"
              onClick={() => setPanel(null)}
            >
              <Layers3 className="size-5" aria-hidden="true" />
              <span>
                {panel === "resources"
                  ? "Review security and product guidance"
                  : "See the complete Resolto workflow"}
              </span>
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        )}
      </div>

      <dialog
        ref={dialogRef}
        className="marketing-mobile-dialog"
        onClick={(event) => {
          if (event.target === dialogRef.current) {
            closeMobile()
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            closeMobile()
          }
        }}
      >
        <div className="marketing-mobile-dialog__header">
          <span className="marketing-wordmark">
            <span className="marketing-wordmark__mark">
              <BrandMark />
            </span>
            <span>Resolto</span>
          </span>
          <button
            type="button"
            className="marketing-icon-button"
            aria-label="Close navigation"
            onClick={closeMobile}
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
        <nav className="marketing-mobile-dialog__links" aria-label="Mobile navigation">
          {[
            ["/product", t("nav.product")],
            ["/how-it-works", t("nav.solutions")],
            ["/product#b2b-saas", t("nav.industries")],
            ["/pricing", t("nav.pricing")],
          ].map(([href, label]) => (
            <Link key={href} href={href ?? "/"} onClick={closeMobile}>
              {label}
            </Link>
          ))}
          <div className="marketing-mobile-dialog__resource-group">
            <strong>{t("footer.resources")}</strong>
            {[
              ["/security", "Security"],
              ["/product", "Product guide"],
              ["/how-it-works", "How it works"],
              ["/integrations", "Integrations"],
              ["/contact", "Contact"],
            ].map(([href, label]) => (
              <Link key={href} href={href ?? "/"} onClick={closeMobile}>
                {label}
              </Link>
            ))}
          </div>
        </nav>
        <div className="marketing-mobile-dialog__utilities">
          <label htmlFor="mobile-locale">{t("nav.language")}</label>
          <select
            id="mobile-locale"
            value={locale}
            onChange={(event) => updateLocale(event.target.value as MarketingLocale)}
          >
            {marketingLocales.map(({ code, label }) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="marketing-mobile-dialog__actions">
          <Link href="/sign-in" onClick={closeMobile}>
            {t("nav.signIn")}
          </Link>
          <Link href="/contact" onClick={closeMobile}>
            {t("cta.secondary")}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
          <Link href={productEntry} onClick={closeMobile}>
            {t("nav.createAccount")}
          </Link>
        </div>
      </dialog>
    </header>
  )
}
